import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Award, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export default function ReviewerLeaderboard() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      setCurrentUser(profile);
    }
  };

  const { data: topReviewers, isLoading } = useQuery({
    queryKey: ["reviewer-leaderboard"],
    queryFn: async () => {
      const { data: feedbackCounts, error } = await supabase
        .from("feedback")
        .select("reviewer_id");
      
      if (error) throw error;

      const reviewerMap = {};
      feedbackCounts.forEach(f => {
        reviewerMap[f.reviewer_id] = (reviewerMap[f.reviewer_id] || 0) + 1;
      });

      const topReviewerIds = Object.entries(reviewerMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 50)
        .map(([id]) => id);

      const { data: profiles, error: profileError } = await supabase
        .from("public_profiles")
        .select("*")
        .in("id", topReviewerIds);

      if (profileError) throw profileError;

      return profiles.map(p => ({
        ...p,
        review_count: reviewerMap[p.id]
      })).sort((a, b) => b.review_count - a.review_count);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getMedalIcon = (rank) => {
    if (rank === 0) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 1) return <Trophy className="w-6 h-6 text-gray-400" />;
    if (rank === 2) return <Trophy className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-bold text-muted-foreground">#{rank + 1}</span>;
  };

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2 flex items-center gap-3">
            <Award className="w-10 h-10" />
            Top Reviewers Leaderboard
          </h1>
          <p className="text-muted-foreground">Community members ranked by feedback contributions</p>
        </div>

        {!topReviewers || topReviewers.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No reviewers on the leaderboard yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topReviewers.map((reviewer, index) => (
              <TooltipProvider key={reviewer.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`bg-card p-6 rounded-lg border transition-all cursor-pointer hover:scale-[1.02] ${
                        index < 3 ? 'border-primary shadow-lg' : 'border-border hover:border-primary'
                      } ${currentUser?.id === reviewer.id ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => navigate(`/PublicProfile?id=${reviewer.id}`)}
                    >
                      <div className="flex items-center gap-6">
                        <div className="flex-shrink-0">{getMedalIcon(index)}</div>
                        <img
                          src={reviewer.avatar_url || "/placeholder.svg"}
                          alt={reviewer.username}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-semibold text-card-foreground">{reviewer.username}</h3>
                            {currentUser?.id === reviewer.id && <Badge variant="default">You</Badge>}
                          </div>
                          {reviewer.bio && <p className="text-muted-foreground mt-1 line-clamp-1">{reviewer.bio}</p>}
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-primary">{reviewer.review_count}</div>
                          <div className="text-sm text-muted-foreground">Reviews</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-foreground">{reviewer.points || 0}</div>
                          <div className="text-sm text-muted-foreground">Points</div>
                        </div>
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View {reviewer.username}'s profile</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}